import { CommonModule } from '@angular/common';
import { Component, computed, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { SessionService } from '../../services/session.service';
import type { MercadoAdsEntity, MercadoAdsMonthOption, MercadoAdsOverview } from '../../models/api.models';

type AdsSelection = 'last90' | string;

@Component({
  selector: 'app-advertising-dashboard-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './advertising-dashboard-page.component.html',
  styleUrl: './advertising-dashboard-page.component.css'
})
export class AdvertisingDashboardPageComponent {
  readonly startDate = signal('');
  readonly endDate = signal('');
  readonly selectedRange = signal<AdsSelection>('last90');
  readonly siteId = signal('MLB');
  readonly months = signal<MercadoAdsMonthOption[]>([]);
  readonly maxRange = signal<{ startDate: string; endDate: string } | null>(null);
  readonly loading = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly message = signal<string | null>(null);
  readonly data = signal<MercadoAdsOverview | null>(null);

  readonly activePeriodLabel = computed(() => `${this.formatDate(this.startDate())} até ${this.formatDate(this.endDate())}`);
  readonly selectedMonth = computed(() => this.months().find((month) => month.month === this.selectedRange()) ?? null);
  readonly topCampaigns = computed(() => [...(this.data()?.campaigns ?? [])].sort((a, b) => b.metrics.cost - a.metrics.cost).slice(0, 12));
  readonly topAds = computed(() => [...(this.data()?.ads ?? [])].sort((a, b) => b.metrics.cost - a.metrics.cost).slice(0, 20));
  readonly weakAds = computed(() =>
    [...(this.data()?.ads ?? [])]
      .filter((ad) => ad.metrics.cost > 0 && (ad.metrics.sales <= 0 || ad.metrics.conversionRate < 1))
      .sort((a, b) => b.metrics.cost - a.metrics.cost)
      .slice(0, 10)
  );
  readonly busy = computed(() => this.loading() || this.syncing());

  constructor(
    private readonly api: ApiService,
    private readonly session: SessionService
  ) {
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    const ok = await this.session.refresh();
    if (!ok) {
      this.error.set('Acesso não autenticado. Entre novamente pelo Cloudflare Access.');
      return;
    }
    await this.loadMonths();
    await this.loadAdsFromDatabase();
  }

  async loadMonths(): Promise<void> {
    const response = await this.api.getMercadoAdsMonths(this.siteId());
    this.months.set(response.months);
    this.maxRange.set(response.maxRange);
    this.startDate.set(response.maxRange.startDate);
    this.endDate.set(response.maxRange.endDate);
  }

  async setRange(value: AdsSelection): Promise<void> {
    this.selectedRange.set(value);
    if (value === 'last90') {
      const range = this.maxRange();
      if (range) {
        this.startDate.set(range.startDate);
        this.endDate.set(range.endDate);
      }
    } else {
      const month = this.months().find((item) => item.month === value);
      if (month) {
        this.startDate.set(month.startDate);
        this.endDate.set(month.endDate);
      }
    }
    await this.loadAdsFromDatabase();
  }

  async loadAdsFromDatabase(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);
    try {
      const response = await this.api.getMercadoAdsOverview(this.startDate(), this.endDate(), this.siteId());
      this.data.set(response);
      this.message.set(
        response.syncedAt
          ? `Dados carregados do banco. Última atualização: ${this.formatDateTime(response.syncedAt)}.`
          : 'Ainda não há dados de publicidade salvos para este período. Clique em Atualizar pela API.'
      );
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao carregar dados salvos de publicidade.');
      this.data.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  async syncSelectedRange(): Promise<void> {
    if (!this.startDate() || !this.endDate()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.message.set('Atualizando publicidade pela API Mercado Ads...');
    try {
      if (this.selectedRange() === 'last90') {
        const months = this.months();
        for (let i = 0; i < months.length; i += 1) {
          const month = months[i];
          this.message.set(`Atualizando ${this.formatMonth(month.month)} (${i + 1}/${months.length})...`);
          await this.api.syncMercadoAdsOverview(month.startDate, month.endDate, this.siteId());
        }
        this.message.set('Últimos 90 dias atualizados e salvos no banco.');
      } else {
        await this.api.syncMercadoAdsOverview(this.startDate(), this.endDate(), this.siteId());
        this.message.set(`${this.formatMonth(this.selectedRange())} atualizado e salvo no banco.`);
      }
      await this.loadMonths();
      await this.setRange(this.selectedRange());
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao atualizar publicidade pela API Mercado Ads.');
    } finally {
      this.syncing.set(false);
    }
  }

  logout(): void {
    window.location.href = '/cdn-cgi/access/logout?returnTo=' + encodeURIComponent(window.location.origin);
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const [year, month, day] = value.slice(0, 10).split('-');
    if (year && month && day) return `${day}/${month}/${year}`;
    return value;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const cleaned = value.replace('T', ' ').replace(/\.\d{3}.*$/, '').replace(/\+.*$/, '').trim();
    const [datePart, timePart] = cleaned.split(' ');
    return `${this.formatDate(datePart)}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
  }

  formatMonth(value: string | null | undefined): string {
    if (!value || value === 'last90') return 'Últimos 90 dias';
    const [year, month] = value.split('-');
    return month && year ? `${month}/${year}` : value;
  }

  formatStatus(value: string | null | undefined): string {
    const normalized = (value ?? '').toLowerCase();
    const map: Record<string, string> = {
      active: 'Ativa',
      paused: 'Pausada',
      deleted: 'Removida',
      enabled: 'Ativa',
      disabled: 'Inativa'
    };
    return map[normalized] ?? value ?? '-';
  }

  healthLabel(entity: MercadoAdsEntity): string {
    if (entity.metrics.cost <= 0) return 'Sem gasto';
    if (entity.metrics.sales <= 0) return 'Gastou sem venda';
    if (entity.metrics.roas < 2) return 'ROAS baixo';
    if (entity.metrics.conversionRate < 1) return 'Conversão baixa';
    return 'Saudável';
  }

  healthClass(entity: MercadoAdsEntity): string {
    const label = this.healthLabel(entity);
    if (label === 'Saudável') return 'ok';
    if (label === 'Sem gasto') return 'neutral';
    if (label === 'Gastou sem venda') return 'danger';
    return 'warning';
  }
}
